import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  type ColumnDef,
  createSortedRowModel,
  flexRender,
  rowSortingFeature,
  type SortingState,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_text,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowLeft, ArrowUp, ArrowUpDown, Download, Minus } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  buildCoachStoreCSVUrl,
  type CoachPeriodFilter,
  type CoachPeriodQuery,
  type CoachSellerSummaryRow,
  type CoachStoreSummary,
  getCoachStoreSummary,
} from '@/api/coach'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { describeErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/coach/stores/$id')({
  component: CoachStoreSummaryPage,
})

type PresetPeriod = CoachPeriodFilter['kind']

// В v9 сортировка — подключаемая фича, а sortFn ищутся только среди
// зарегистрированных: авто-выбор для строк берёт text/alphanumeric,
// без них сортировка по имени молча откатывается к basic.
const sellersTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, basic: sortFn_basic, text: sortFn_text },
})

type SellersTableFeatures = typeof sellersTableFeatures
type SellerColumnDef = ColumnDef<SellersTableFeatures, CoachSellerSummaryRow>

const PERIOD_LABEL: Record<PresetPeriod, string> = {
  week: 'Неделя',
  month: 'Месяц',
}

function CoachStoreSummaryPage() {
  const { id: storeId } = Route.useParams()
  const [period, setPeriod] = useState<PresetPeriod>('week')

  const periodQuery: CoachPeriodQuery = { kind: period }
  const query = useQuery({
    queryKey: ['coach', 'summary', storeId, period],
    queryFn: () => getCoachStoreSummary(storeId, periodQuery),
  })

  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-6 pb-12">
      <Link
        to="/coach/stores"
        className="-ml-3 inline-flex w-fit items-center gap-2 rounded-md px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />К списку магазинов
      </Link>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {query.data?.store.name ?? 'Магазин'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {query.data?.store.city ?? 'Сводка по продавцам за выбранный период.'}
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <PeriodToggle value={period} onChange={setPeriod} />
          <CSVDownloadButton
            storeId={storeId}
            periodQuery={periodQuery}
            disabled={!query.data || query.data.sellers.length === 0}
          />
        </div>
      </header>

      {query.isLoading ? (
        <StatusCard title="Загружаем сводку" description="Это займёт секунду." />
      ) : query.isError ? (
        <StatusCard
          title="Не удалось загрузить сводку"
          description={describeErrorMessage(query.error, 'Попробуйте обновить.')}
        />
      ) : query.data ? (
        <SummaryBody summary={query.data} />
      ) : null}
    </main>
  )
}

interface PeriodToggleProps {
  value: PresetPeriod
  onChange: (next: PresetPeriod) => void
}

function PeriodToggle({ value, onChange }: PeriodToggleProps) {
  return (
    <div
      role="tablist"
      aria-label="Период сводки"
      className="inline-flex items-center rounded-lg border bg-muted p-1"
    >
      {(['week', 'month'] as PresetPeriod[]).map((kind) => {
        const active = kind === value
        return (
          <button
            key={kind}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(kind)}
            className={cn(
              'rounded-md px-3 py-1 text-sm font-medium transition-colors',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {PERIOD_LABEL[kind]}
          </button>
        )
      })}
    </div>
  )
}

interface SummaryBodyProps {
  summary: CoachStoreSummary
}

function SummaryBody({ summary }: SummaryBodyProps) {
  if (summary.sellers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>В магазине пока нет продавцов</CardTitle>
          <CardDescription>Попросите администратора привязать продавца к магазину.</CardDescription>
        </CardHeader>
      </Card>
    )
  }
  return <SellersTable sellers={summary.sellers} />
}

interface SellersTableProps {
  sellers: CoachSellerSummaryRow[]
}

function SellersTable({ sellers }: SellersTableProps) {
  const navigate = useNavigate()
  // Родительский $id магазина нужен, чтобы «назад» из деталки продавца
  // возвращало в тот же магазин с теми же фильтрами.
  const { id: storeId } = Route.useParams()
  const [sorting, setSorting] = useState<SortingState>([{ id: 'fullName', desc: false }])
  const columns = useMemo<SellerColumnDef[]>(() => buildColumns(), [])
  const table = useTable({
    features: sellersTableFeatures,
    data: sellers,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Продавцы магазина</CardTitle>
        <CardDescription>Клик на строку — детали продавца за тот же период.</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/40 text-left">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => {
                  const sort = header.column.getIsSorted()
                  const canSort = header.column.getCanSort()
                  const ariaSort =
                    sort === 'asc'
                      ? 'ascending'
                      : sort === 'desc'
                        ? 'descending'
                        : ('none' as const)
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className="px-4 py-3 font-medium text-muted-foreground"
                      aria-sort={canSort ? ariaSort : undefined}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <SortIcon sort={sort || null} />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer border-t transition-colors hover:bg-accent"
                onClick={() =>
                  navigate({
                    to: '/coach/sellers/$id',
                    params: { id: row.original.sellerId },
                    // storeId приходит обратно через search-параметр, когда
                    // пользователь жмёт «Назад» на странице продавца.
                    search: { storeId },
                  })
                }
              >
                {row.getAllCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 align-top">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

interface CSVDownloadButtonProps {
  storeId: string
  periodQuery: CoachPeriodQuery
  disabled?: boolean
}

// Браузер сам тянет CSV по ссылке (с cookie-сессией), поэтому `<a download>`
// достаточно — не нужно делать fetch + blob + URL.createObjectURL. Download
// подтягивает тот же фильтр периода, что и таблица на экране (plan §5.3).
function CSVDownloadButton({ storeId, periodQuery, disabled }: CSVDownloadButtonProps) {
  const href = buildCoachStoreCSVUrl(storeId, periodQuery)
  return (
    <Button
      asChild
      variant="outline"
      size="sm"
      // aria-disabled, а не disabled на <a>, иначе клик по стрелке tab проходит.
      aria-disabled={disabled || undefined}
      className={cn('gap-2', disabled ? 'pointer-events-none opacity-50' : undefined)}
    >
      <a href={href} download>
        <Download className="h-4 w-4" aria-hidden="true" />
        Скачать CSV
      </a>
    </Button>
  )
}

function SortIcon({ sort }: { sort: 'asc' | 'desc' | null }) {
  if (sort === 'asc') return <ArrowUp className="h-3 w-3" aria-hidden="true" />
  if (sort === 'desc') return <ArrowDown className="h-3 w-3" aria-hidden="true" />
  return <ArrowUpDown className="h-3 w-3 opacity-50" aria-hidden="true" />
}

function buildColumns(): SellerColumnDef[] {
  return [
    {
      id: 'fullName',
      header: 'Продавец',
      accessorFn: (row) => row.fullName,
      cell: ({ row }) => (
        <span className="font-medium text-foreground">{row.original.fullName}</span>
      ),
    },
    {
      id: 'consultationsCount',
      header: 'Консультаций',
      accessorFn: (row) => row.consultationsCount,
      cell: ({ row }) => (
        <CountCell
          value={row.original.consultationsCount}
          delta={row.original.consultationsDelta}
          previous={row.original.previousCount}
        />
      ),
    },
    {
      id: 'avgScorePercent',
      header: 'Средний балл',
      accessorFn: (row) => row.avgScorePercent ?? -1,
      sortFn: 'basic',
      cell: ({ row }) => (
        <ScoreCell
          value={row.original.avgScorePercent}
          delta={row.original.avgScoreDelta}
          previous={row.original.previousAvgScorePercent}
        />
      ),
    },
    {
      id: 'topTags',
      header: 'Топ теги',
      // accessorFn возвращает строку для sort по первому тегу.
      accessorFn: (row) => row.topTags[0]?.label ?? '',
      cell: ({ row }) =>
        row.original.topTags.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.original.topTags.map((tag) => (
              <span
                key={tag.code}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
              >
                {tag.label}
                <span className="text-muted-foreground">· {tag.count}</span>
              </span>
            ))}
          </div>
        ),
    },
  ]
}

interface CountCellProps {
  value: number
  delta: number
  previous: number
}

function CountCell({ value, delta, previous }: CountCellProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium tabular-nums">{value}</span>
      <DeltaChip delta={delta} suffix="" previousLabel={`Было: ${previous}`} />
    </div>
  )
}

interface ScoreCellProps {
  value: number | null
  delta: number | null
  previous: number | null
}

function ScoreCell({ value, delta, previous }: ScoreCellProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium tabular-nums">{formatScore(value)}</span>
      <DeltaChip
        delta={delta}
        suffix="%"
        previousLabel={
          previous === null ? 'Нет данных прошлого периода' : `Было: ${formatScore(previous)}`
        }
      />
    </div>
  )
}

interface DeltaChipProps {
  delta: number | null
  suffix: string
  previousLabel: string
}

// DeltaChip повторяет семантику /seller/analytics: зелёный/красный знак +
// направление + значение. null = «нет данных», рисуем «—» без цвета.
function DeltaChip({ delta, suffix, previousLabel }: DeltaChipProps) {
  if (delta === null) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  const rounded = Math.round(delta * 10) / 10
  if (rounded === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
        role="status"
        aria-label={`Без изменений. ${previousLabel}`}
      >
        <Minus className="h-3 w-3" aria-hidden="true" />0{suffix}
      </span>
    )
  }
  const positive = rounded > 0
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        positive ? 'bg-emerald-500/10 text-emerald-600' : 'bg-destructive/10 text-destructive',
      )}
      role="status"
      aria-label={`Изменение: ${positive ? '+' : ''}${rounded}${suffix}. ${previousLabel}`}
    >
      {positive ? (
        <ArrowUp className="h-3 w-3" aria-hidden="true" />
      ) : (
        <ArrowDown className="h-3 w-3" aria-hidden="true" />
      )}
      {positive ? '+' : ''}
      {rounded}
      {suffix}
    </span>
  )
}

function StatusCard({ title, description }: { title: string; description: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
    </Card>
  )
}

function formatScore(value: number | null): string {
  if (value === null) return '—'
  return `${Math.round(value)}%`
}
